import { CIRCULAR_KNITTING_LEG } from "shared/constants/productScope";

export type LibraryKind = "Default" | "Workspace";

export const DEFAULT_MAIN_PRODUCT_LINE = CIRCULAR_KNITTING_LEG.mainProductLine;

export const isDefaultLibrary = (library?: { kind?: LibraryKind }): boolean =>
  library?.kind === "Default";

export interface IKnittingProgramLibrary {
  id: string;
  name: string;
  description: string;
  kind: LibraryKind;
  mainProductLine: string;
  programCount: number;
  modifiedBy?: string;
  modifiedAt?: string;
}

export interface IKnittingProgramLibraryResponse {
  data: IKnittingProgramLibrary[];
  count: number;
  pageSize: number;
  defaultLibrary: IKnittingProgramLibrary;
}

export interface IKnittingProgram {
  id: string;
  libraryId: string;
  programName: string;
  programType: string;
  bundlePart?: string;
  groupedCc4?: boolean;
  cc4PartPresence?: {
    cc4: boolean;
    med: boolean;
    inf: boolean;
  };
  machineType: string;
  isDeleted: boolean;
  version: number;
  modifiedBy?: string;
  modifiedAt?: string;
}

export interface IKnittingProgramResponse {
  data: IKnittingProgram[];
  count: number;
  pageSize: number;
}

export interface IKnittingProgramLibraryDetails {
  id: string;
  name: string;
  description: string;
  kind: LibraryKind;
  mainProductLine: string;
}

export interface IKnittingProgramLibraryVersion {
  version: number;
  comment: string;
  modifiedAt: string;
  modifiedBy: string;
}

export interface IKnittingProgramVersion {
  version: number;
  comment: string;
  programName: string;
  libraryId: string;
  modifiedAt: string;
  modifiedBy: string;
  blobPath: string;
  isDeleted: boolean;
  bundlePart?: string;
}

/**
 * Upload is a three-phase action: start claims the library, files stages batches without
 * touching it, and commit materialises everything and advances the library version once.
 * The session id is what ties those requests together into a single action.
 */
export type KnittingProgramUploadSessionState =
  | "Staging"
  | "Committed"
  | "Aborted";

export type KnittingProgramUploadItemState =
  | "Staged"
  | "Failed"
  | "Skipped"
  | "Committed";

export interface IKnittingProgramUploadSession {
  sessionId: string;
  libraryId: string;
  comment: string;
  startedBy: string;
  startedAt: string;
  state: KnittingProgramUploadSessionState;
}

export interface IStageKnittingProgramResult {
  fileName: string;
  programName: string;
  programType: string;
  bundlePart?: string;
  state: KnittingProgramUploadItemState;
  targetVersion: number;
  /** True when the file is byte-identical to what the library already holds. */
  unchanged: boolean;
  errorMessage?: string;
}

export interface ICommitKnittingProgramUploadResult {
  /** Always exactly one higher than before, or unchanged when nothing was committed. */
  libraryVersion: number;
  committed: number;
  skipped: number;
  discarded: number;
}

/** Backend ErrorCode values the upload flow reacts to specifically rather than just reporting. */
export const KNITTING_PROGRAM_UPLOAD_ERROR = {
  SESSION_CONFLICT: 810,
  SESSION_NOT_FOUND: 811,
  SESSION_NOT_STAGING: 812,
  STALE_VERSION: 813,
  HAS_FAILED_ITEMS: 814,
} as const;

/** Who is holding the library, so the user can decide whether to take it over. */
export interface IUploadSessionConflict {
  sessionId?: string;
  startedBy?: string;
  startedAt?: string;
}

export type UploadRunPhase =
  /** The library could not be claimed at all; nothing was uploaded. */
  | "startFailed"
  /** Someone else is uploading into this library right now. */
  | "startConflict"
  /** Files are staged but some failed, so the user must choose whether to discard them. */
  | "awaitingConfirmation"
  /** Everything staged, but turning it into programs failed. The session is still open. */
  | "commitFailed"
  /** Done: programs are live and the library advanced by exactly one version. */
  | "committed";

export interface IUploadRunResult {
  phase: UploadRunPhase;
  sessionId?: string;
  results: IStageKnittingProgramResult[];
  commit?: ICommitKnittingProgramUploadResult;
  errorMessage?: string;
  conflict?: IUploadSessionConflict;
  /** The session handle is stale; the next attempt must start a fresh action. */
  sessionGone?: boolean;
}

export interface IFileCheckResult {
  fileName: string;
  fileSize: number;
  isNew: boolean;
  alreadyExists: boolean;
  currentVersion?: number;
  nextVersion: number;
  hash?: string;
}

export interface ICreateKnittingProgramLibraryCommand {
  name: string;
  mainProductLine: string;
  description?: string;
}

export interface IUpdateKnittingProgramLibraryCommand {
  id: string;
  name: string;
  description?: string;
}

export interface IRevertCommand {
  targetVersion: number;
  comment: string;
}

export interface IRevertResult {
  reverted: boolean;
  targetVersion: number;
  currentVersion: number;
}

export interface IKnittingProgramLibraryDeleteReadiness {
  libraryId: string;
  missingFiles: string[];
  missingFileCount: number;
}
